import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import { QuoteChangeLogView } from "../../features/quotes/change-log/QuoteChangeLogView.js";
import { QuoteSubHeader } from "../../features/quotes/QuoteSubHeader.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { useProjectQuoteChangeLogQuery } from "./ProjectQuoteChangeLog.generated.js";

interface Props {
  projectId: string;
  quoteId: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
  pathToDocList: string;
  pathToDoc: string;
}

export function ProjectQuoteChangeLog({
  projectId,
  quoteId,
  submenuItems,
  prefixBreadcrumbs,
  activeSubmenuItem,
  pathToDocList,
  pathToDoc,
}: Props) {
  const { t } = useTranslate(["Global"]);

  const client = useApolloClient();
  const query = useProjectQuoteChangeLogQuery({
    client,
    variables: { projectId, docId: quoteId },
  });
  const project = getDataOrNull(query.data?.project)?.project;
  const quote = getDataOrNull(query.data?.quote)?.quote;

  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  return (
    <Page
      title={quote?.title ?? t("Change log", { ns: "Global" })}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      header={
        <PageHeader
          breadcrumbs={prefixBreadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      subHeader={
        project && quote ? (
          <QuoteSubHeader
            project={project}
            quote={quote}
            activeView="change-log"
            canEdit={false}
            isReadOnly={true}
            pathToDocList={pathToDocList}
            pathToDoc={pathToDoc}
            hasSapS4HanaIntegration={hasSapS4HanaIntegration}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
            expandedItemIds={undefined}
          />
        ) : undefined
      }
    >
      <PageContainer>
        <QuoteChangeLogView
          projectId={projectId}
          docId={quoteId}
          pathToDoc={pathToDoc}
        />
      </PageContainer>
    </Page>
  );
}
